import React, { createContext, useContext, useState, useEffect } from "react";
import * as Linking from "expo-linking";
import { Platform } from "react-native";

interface InitialUrlContextValue {
  initialUrl: string | null;
  resetInitialUrl: () => void;
}

const InitialUrlContext = createContext<InitialUrlContextValue | undefined>(
  undefined
);

const getWebUrl = () =>
  Platform.OS === "web" && typeof window !== "undefined"
    ? window.location.href
    : null;

export const InitialUrlProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const [initialUrl, setInitialUrl] = useState<string | null>(getWebUrl);

  useEffect(() => {
    if (Platform.OS !== "web") {
      const fetchInitialUrl = async () => {
        const url = await Linking.getInitialURL();
        setInitialUrl(url);
      };
      fetchInitialUrl();
    }

    const urlSubscription = Linking.addEventListener("url", ({ url }) => {
      if (Platform.OS === "web" && typeof window !== "undefined") {
        const currentUrl = window.location.href;
        if (url === currentUrl) return;
      }
      setInitialUrl((prev) => {
        if (!prev) return url;
        return prev;
      });
    });

    return () => {
      urlSubscription.remove();
    };
  }, []);

  const resetInitialUrl = () => {
    if (!initialUrl?.includes("review-page")) {
      setInitialUrl(null);
    }
  };

  return (
    <InitialUrlContext.Provider value={{ initialUrl, resetInitialUrl }}>
      {children}
    </InitialUrlContext.Provider>
  );
};

export const useInitialUrlContext = (): InitialUrlContextValue => {
  const context = useContext(InitialUrlContext);
  if (!context) {
    throw new Error(
      "useInitialUrlContext must be used within an InitialUrlProvider"
    );
  }
  return context;
};
